/**
 * TributeHeroStrip — compact one-click tribute row displayed under the
 * memorial name in the hero. Emoji buttons + live totals + optimistic
 * count bump.
 *
 * Uses the shared /api/memorials/{key}/tributes endpoints. Every click
 * posts a bare tribute (no note) so visitors can leave a token in a
 * single tap; the full note-writing widget lives lower on the page.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import TributeThankYouModal from "@/components/TributeThankYouModal";

const TOKENS = [
  { key: "candle",         label: "candle",         plural: "candles",         emoji: "🕯" },
  { key: "rose",           label: "rose",           plural: "roses",           emoji: "🌹" },
  { key: "carnation",      label: "carnation",      plural: "carnations",      emoji: "🌷" },
  { key: "cherry_blossom", label: "cherry blossom", plural: "cherry blossoms", emoji: "🌸" },
  { key: "bouquet",        label: "bouquet",        plural: "bouquets",        emoji: "💐" },
  { key: "dove",           label: "dove",           plural: "doves",           emoji: "🕊" },
  { key: "prayer",         label: "prayer",         plural: "prayers",         emoji: "🙏" },
];

export default function TributeHeroStrip({ slug, initialCounts, memorialName, memorialYears }) {
  const [counts, setCounts] = useState(initialCounts || {});
  const [busy, setBusy] = useState(null);
  const [thankYou, setThankYou] = useState(null);   // { kind, ... } | null

  // Refresh once on mount so the totals reflect the latest state even if
  // the memorial doc was cached.
  useEffect(() => {
    let alive = true;
    http.get(`/memorials/${slug}/tributes`, { params: { limit: 1 } })
      .then((r) => { if (alive) setCounts(r.data?.counts || {}); })
      .catch(() => { /* leave optimistic counts */ });
    return () => { alive = false; };
  }, [slug]);

  const total = TOKENS.reduce((a, t) => a + (counts[t.key] || 0), 0);

  const tap = async (kind) => {
    setBusy(kind);
    // Optimistic bump
    setCounts((c) => ({ ...c, [kind]: (c[kind] || 0) + 1 }));
    try {
      await http.post(`/memorials/${slug}/tributes`, { kind });
      // Offer a downloadable keepsake card the visitor can share
      setThankYou({ kind, memorialName, memorialYears });
    } catch (e) {
      // Roll back on failure
      setCounts((c) => ({ ...c, [kind]: Math.max(0, (c[kind] || 1) - 1) }));
      toast.error(e?.response?.data?.detail || "Couldn't leave your tribute.");
    } finally {
      setBusy(null);
    }
  };

  // Compact tally line: only tokens with counts > 0, oxford-ish list
  const tally = TOKENS
    .filter((t) => (counts[t.key] || 0) > 0)
    .map((t) => `${(counts[t.key] || 0).toLocaleString()} ${(counts[t.key] || 0) === 1 ? t.label : t.plural}`);

  return (
    <div className="mt-3" data-testid="mem-hero-tribute-strip">
      <div className="flex flex-wrap items-center gap-1.5">
        {TOKENS.map((t) => {
          const n = counts[t.key] || 0;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => tap(t.key)}
              disabled={busy === t.key}
              title={`Leave a ${t.label}`}
              className={`group inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-sm bg-white hover:bg-amber-50 hover:border-amber-300 transition-all active:scale-95 ${busy === t.key ? "opacity-60" : ""}`}
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid={`mem-hero-tribute-${t.key}`}
            >
              <span className="text-lg leading-none group-hover:scale-125 transition-transform" aria-hidden="true">{t.emoji}</span>
              <span className="text-[11px] font-mono opacity-70 min-w-[1ch] tabular-nums">{n.toLocaleString()}</span>
            </button>
          );
        })}
      </div>
      {total > 0 && (
        <div className="mt-1.5 text-[11px] italic" style={{ color: "var(--wofy-text-soft)" }} data-testid="mem-hero-tribute-summary">
          {tally.slice(0, 3).join(" · ")}{tally.length > 3 ? " · +" + (tally.length - 3) + " more" : ""} left in memory
        </div>
      )}
      <TributeThankYouModal open={thankYou} onClose={() => setThankYou(null)} />
    </div>
  );
}
